import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { API, assetSrc } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { RichTextEditor } from "@/components/RichTextEditor";
import { FloppyDisk, Trash, ArrowLeft, UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";

const INPUT = "w-full border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors duration-200";
const LABEL = "text-xs font-bold uppercase tracking-wider text-muted-foreground";

const CoverField = ({ value, onChange }) => {
  const { t } = useLang();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/uploads/image`, fd);
      onChange(data.url);
      toast.success(t.saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.saveFailed);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div data-testid="blog-cover">
      <div className={`${LABEL} mb-2`}>{t.articleCover}</div>
      <div className="flex items-start gap-4">
        <div className="flex h-24 w-40 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">
          {value ? <img src={assetSrc(value)} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <ImageIcon size={28} className="text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="blog-cover-file" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-1.5 bg-primary px-3 py-2 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
            <UploadSimple size={15} weight="bold" /> {uploading ? t.uploading : t.uploadImage}
          </button>
          <input data-testid="blog-cover-url" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={t.orUrl} className={INPUT} />
        </div>
      </div>
    </div>
  );
};

export default function BlogEditPage() {
  const { t } = useLang();
  const { postId } = useParams();
  const isNew = !postId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [p, setP] = useState({
    title_fr: "", title_en: "", excerpt_fr: "", excerpt_en: "",
    body_fr: "", body_en: "", cover_image: "", slug: "", status: "draft",
  });

  useEffect(() => {
    if (isNew) return;
    let ignore = false;
    axios.get(`${API}/admin/blog/${postId}`)
      .then((r) => { if (!ignore) setP(r.data); })
      .catch(() => { if (!ignore) toast.error(t.saveFailed); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [postId]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!p.title_fr.trim() && !p.title_en.trim()) { toast.error(t.articleTitleFr); return; }
    setSaving(true);
    const body = {
      title_fr: p.title_fr || "", title_en: p.title_en || "",
      excerpt_fr: p.excerpt_fr || "", excerpt_en: p.excerpt_en || "",
      body_fr: p.body_fr || "", body_en: p.body_en || "",
      cover_image: p.cover_image || "", slug: p.slug || "",
      status: p.status || "draft",
    };
    try {
      if (isNew) await axios.post(`${API}/admin/blog`, body);
      else await axios.put(`${API}/admin/blog/${postId}`, body);
      toast.success(t.saved);
      navigate("/admin/website?tab=blog");
    } catch (e) {
      toast.error(e.response?.data?.detail || t.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t.confirmDeleteArticle)) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`);
      toast.success(t.saved);
      navigate("/admin/website?tab=blog");
    } catch (e) {
      toast.error(e.response?.data?.detail || t.saveFailed);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/admin/website?tab=blog")} data-testid="back-to-blog"
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-primary">
          <ArrowLeft size={15} weight="bold" /> {t.blog}
        </button>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
          <h1 className="font-display text-3xl font-black tracking-tighter text-foreground">{isNew ? t.newArticle : t.editArticle}</h1>
          <div className="flex items-center gap-2">
            {!isNew && (
              <button data-testid="delete-article-button" onClick={remove}
                className="inline-flex items-center gap-2 border border-red-400 px-4 py-3 text-xs font-bold uppercase tracking-wider text-red-500 hover:bg-red-500 hover:text-white transition-colors duration-200">
                <Trash size={15} weight="bold" /> {t.deleteArticle}
              </button>
            )}
            <button data-testid="save-article-button" onClick={save} disabled={saving || loading}
              className="inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              <FloppyDisk size={16} weight="bold" /> {t.save}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground">{t.loading}</div>
        ) : (
          <div className="space-y-6 border border-border bg-card p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className={`${LABEL} mb-2`}>{t.articleTitleFr}</div>
                <input data-testid="blog-title-fr" value={p.title_fr} onChange={(e) => set("title_fr", e.target.value)} className={INPUT} />
              </div>
              <div>
                <div className={`${LABEL} mb-2`}>{t.articleTitleEn}</div>
                <input data-testid="blog-title-en" value={p.title_en} onChange={(e) => set("title_en", e.target.value)} className={INPUT} />
              </div>
            </div>

            <CoverField value={p.cover_image} onChange={(v) => set("cover_image", v)} />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className={`${LABEL} mb-2`}>{t.articleExcerptFr}</div>
                <textarea data-testid="blog-excerpt-fr" rows={3} value={p.excerpt_fr} onChange={(e) => set("excerpt_fr", e.target.value)} className={INPUT} />
              </div>
              <div>
                <div className={`${LABEL} mb-2`}>{t.articleExcerptEn}</div>
                <textarea data-testid="blog-excerpt-en" rows={3} value={p.excerpt_en} onChange={(e) => set("excerpt_en", e.target.value)} className={INPUT} />
              </div>
            </div>

            <div>
              <div className={`${LABEL} mb-2`}>{t.articleBodyFr}</div>
              <RichTextEditor testid="blog-body-fr" value={p.body_fr} onChange={(v) => set("body_fr", v)} placeholder="…" />
            </div>
            <div>
              <div className={`${LABEL} mb-2`}>{t.articleBodyEn}</div>
              <RichTextEditor testid="blog-body-en" value={p.body_en} onChange={(v) => set("body_en", v)} placeholder="…" />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className={`${LABEL} mb-2`}>{t.articleSlug}</div>
                <input data-testid="blog-slug" value={p.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto" className={INPUT} />
              </div>
              <div>
                <div className={`${LABEL} mb-2`}>{t.articleStatus}</div>
                <select data-testid="blog-status" value={p.status} onChange={(e) => set("status", e.target.value)} className={INPUT}>
                  <option value="draft">{t.statusDraft}</option>
                  <option value="published">{t.statusPublished}</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
